import Grid from '@material-ui/core/Grid'
import Image from 'next/legacy/image'
import React, { MutableRefObject, PropsWithChildren, useRef } from 'react'
import { useContainerDimensions } from '../../utils/dimension'
import {
    StyledGlossaryCard,
    StyledGlossaryCardContent,
    StyledGlossaryCardHeading,
    StyledGlossaryCardImageContainer,
    StyledGlossaryCardSegment,
    StyledGlossaryCardWrapper,
    StyledGlossaryHr,
    StyledGlossaryTitle,
} from './StyledGlossary'

export type GlossaryTitleProps = {
    
} & React.ComponentProps<typeof StyledGlossaryTitle>

export type GlossaryHrProps = {
    
} & React.ComponentProps<typeof StyledGlossaryHr>

export type GlossaryCardSegmentProps = {
  children: React.ReactNode[]
} & React.ComponentProps<typeof StyledGlossaryCardSegment>

export type GlossaryCardImageProps = {
    src?: string
} & React.ComponentProps<typeof Grid>

export type GlossaryCardHeadingProps = {
    
} & React.ComponentProps<typeof StyledGlossaryCardHeading>

export type GlossaryCardContentProps = {
  //
} & React.ComponentProps<typeof StyledGlossaryCardContent>

export type GlossaryCardWrapperProps = {
} & React.ComponentProps<typeof StyledGlossaryCardWrapper>

const GlossaryTitle : React.FC<PropsWithChildren<GlossaryTitleProps>> = (props) => {
  return (
    <StyledGlossaryTitle {...props} variant="h5">
      {props.children}  
    </StyledGlossaryTitle>
  )
}

const GlossaryHr : React.FC<PropsWithChildren<GlossaryHrProps>> = (props) => {
  return (
    <StyledGlossaryHr {...props} >
      {props.children}  
    </StyledGlossaryHr>
  )
}

const GlossaryCardSegment : React.FC<PropsWithChildren<GlossaryCardSegmentProps>> = ({ children, ...props }) => {
  if (!children) {
    return null
  }

  return (
    <StyledGlossaryCardSegment {...props} >
      {children}  
    </StyledGlossaryCardSegment>
  )
}

const GlossaryCardWrapper : React.FC<PropsWithChildren<GlossaryCardWrapperProps>> = ({ children, ...props }) => {
  if (!children) {
    return null
  }

  return (
    <StyledGlossaryCardWrapper {...props} >
      {children}  
    </StyledGlossaryCardWrapper>
  )
}

const GlossaryCardImage : React.FC<PropsWithChildren<GlossaryCardImageProps>> = ({ src, ...props }) => { 
  const useDimensionRef = useRef() as MutableRefObject<HTMLDivElement>
  const { width } = useContainerDimensions(useDimensionRef)

  if(src== undefined){
    return null
  }

  return (
    <Grid {...props} ref={useDimensionRef}>
      <StyledGlossaryCardImageContainer $width={width}>
        <Image src={src} alt="" layout="fill" objectFit="cover" objectPosition="top right" />
      </StyledGlossaryCardImageContainer>
    </Grid>
  )
}

const GlossaryCardHeading : React.FC<PropsWithChildren<GlossaryCardHeadingProps>> = (props) => {
  return (
    <StyledGlossaryCardHeading {...props} variant="h3">
      {props.children}  
    </StyledGlossaryCardHeading>
  )
}

const GlossaryCardContent : React.FC<PropsWithChildren<GlossaryCardContentProps>> = ({ ...props }) => {
  return (
    <StyledGlossaryCardContent {...props}>
      {props.children}  
    </StyledGlossaryCardContent>
  )
}

export type GlossaryCardProps = { title?: boolean; hr?: boolean; segment?: boolean; wrapper?: boolean; image?: boolean; heading?: boolean; content?: boolean } & (
    | ({ title: true} & GlossaryTitleProps)
    | ({ hr: true} & GlossaryHrProps)
    | ({ segment: true} & GlossaryCardSegmentProps)
    | ({ wrapper: true} & GlossaryCardWrapperProps)
    | ({ image: true} & GlossaryCardImageProps)
    | ({ heading: true} & GlossaryCardHeadingProps)
    | ({ content: true} & GlossaryCardContentProps)
    | & React.ComponentProps<typeof StyledGlossaryCard>
)

export const GlossaryCard: React.FC<PropsWithChildren<GlossaryCardProps>> =  ({ title, hr, segment, wrapper, image, heading, content,...props }) => {
  if(title){
    return <GlossaryTitle { ...props}>{props.children}</GlossaryTitle>
  }

  if(hr){
    return <GlossaryHr { ...props}>{props.children}</GlossaryHr>
  }

  if(segment){
    return (
      <GlossaryCardSegment { ...props}>
        {props.children}
        <React.Fragment></React.Fragment>
      </GlossaryCardSegment>
    )
  }

  if(image){
    return <GlossaryCardImage { ...props}/>
  }

  if(heading){
    return <GlossaryCardHeading { ...props}>{props.children}</GlossaryCardHeading>
  }

  if(content){
    return <GlossaryCardContent { ...props}>{props.children}</GlossaryCardContent>
  }

  if(wrapper){
    return <GlossaryCardWrapper { ...props}>{props.children}</GlossaryCardWrapper>
  }

  return (
    <StyledGlossaryCard { ...props}>{props.children}</StyledGlossaryCard>
  )
}

